<section class="settings-page">
  <header class="settings-hero d-flex align-items-center gap-3 mb-4">
    <div class="settings-hero-icon" aria-hidden="true">
      <fa-icon [icon]="faUser"></fa-icon>
    </div>
    <div>
      <h1 class="settings-title fw-bold mb-1">Поставки</h1>
      <p class="text-muted mb-0">Управувајте со податоците за клиентот и безбедноста на вашата сметка.</p>
    </div>
  </header>

  <div class="settings-layout app-card">
    <nav class="settings-tabs" aria-label="Категории на поставки" role="tablist">
      <button
        type="button"
        class="settings-tab"
        [class.active]="activeTab === 'client'"
        [attr.aria-selected]="activeTab === 'client'"
        (click)="selectTab('client')"
        role="tab">
        <span class="settings-tab-icon"><fa-icon [icon]="faBuilding"></fa-icon></span>
        <span class="text-start">
          <span class="d-block fw-bold">Клиентски податоци</span>
          <small class="d-block">Контакт и адреса</small>
        </span>
      </button>

      <button
        type="button"
        class="settings-tab"
        [class.active]="activeTab === 'security'"
        [attr.aria-selected]="activeTab === 'security'"
        (click)="selectTab('security')"
        role="tab">
        <span class="settings-tab-icon"><fa-icon [icon]="faShieldHalved"></fa-icon></span>
        <span class="text-start">
          <span class="d-block fw-bold">Лозинка и безбедност</span>
          <small class="d-block">Заштита на сметката</small>
        </span>
      </button>
    </nav>

    <div class="settings-content">
      @if (loading) {
        <div class="settings-loading d-flex align-items-center justify-content-center">
          <app-loading-spinner></app-loading-spinner>
        </div>
      } @else if (loadError) {
        <div class="settings-loading d-flex flex-column align-items-center justify-content-center gap-3">
          <app-error-alert class="d-block w-100" title="Поставките не се вчитани" [message]="loadError"></app-error-alert>
          <button type="button" class="btn primary-outline-btn rounded-4 px-4" (click)="loadSettings()">
            Обиди се повторно
          </button>
        </div>
      } @else if (activeTab === 'client') {
        <div role="tabpanel">
          <div class="settings-section-heading d-flex align-items-start justify-content-between gap-3 mb-4">
            <div>
              <h2 class="h4 fw-bold mb-1">Податоци за клиентот</h2>
              <p class="text-muted mb-0">Овие податоци го претставуваат вашиот бизнис во системот.</p>
            </div>
            <span class="settings-account-chip">
              <fa-icon [icon]="faUser"></fa-icon>
              {{ settings?.userEmail }}
            </span>
          </div>

          @if (clientError) {
            <app-error-alert class="d-block mb-4" title="Промените не се зачувани" [message]="clientError"></app-error-alert>
          }
          @if (clientSuccess) {
            <div class="alert settings-success-alert d-flex align-items-center gap-2 mb-4" role="status">
              <fa-icon [icon]="faCheck"></fa-icon>
              <span>{{ clientSuccess }}</span>
            </div>
          }

          <form #clientSettingsForm="ngForm" (ngSubmit)="saveClient(clientSettingsForm)" novalidate>
            <div class="row g-4">
              <div class="col-12 col-lg-6">
                <label class="form-label fw-semibold" for="clientName">
                  <fa-icon class="settings-label-icon" [icon]="faBuilding"></fa-icon>
                  Име на клиент
                </label>
                <input
                  class="form-control settings-control"
                  [class.is-invalid]="clientName.invalid && (clientName.touched || clientSettingsForm.submitted)"
                  id="clientName"
                  name="name"
                  [(ngModel)]="clientForm.name"
                  #clientName="ngModel"
                  autocomplete="organization"
                  maxlength="200"
                  required/>
                @if (clientName.invalid && (clientName.touched || clientSettingsForm.submitted)) {
                  <div class="invalid-feedback">Името е задолжително.</div>
                }
              </div>

              <div class="col-12 col-lg-6">
                <label class="form-label fw-semibold" for="clientPhone">
                  <fa-icon class="settings-label-icon" [icon]="faPhone"></fa-icon>
                  Телефон
                </label>
                <input
                  class="form-control settings-control"
                  [class.is-invalid]="clientPhone.invalid && (clientPhone.touched || clientSettingsForm.submitted)"
                  id="clientPhone"
                  name="phoneNumber"
                  [ngModel]="clientForm.phoneNumber"
                  (ngModelChange)="updatePhoneNumber($event)"
                  #clientPhone="ngModel"
                  type="tel"
                  inputmode="tel"
                  autocomplete="tel"
                  maxlength="50"
                  [pattern]="phonePattern"
                  required/>
                @if (clientPhone.errors?.['required'] && (clientPhone.touched || clientSettingsForm.submitted)) {
                  <div class="invalid-feedback">Телефонскиот број е задолжителен.</div>
                } @else if (clientPhone.errors?.['pattern'] && (clientPhone.touched || clientSettingsForm.submitted)) {
                  <div class="invalid-feedback">Внесете само бројки, со незадолжителен + на почетокот.</div>
                }
              </div>

              <div class="col-12">
                <label class="form-label fw-semibold" for="clientLocation">
                  <fa-icon class="settings-label-icon" [icon]="faLocationDot"></fa-icon>
                  Адреса
                </label>
                <input
                  class="form-control settings-control"
                  [class.is-invalid]="clientLocation.invalid && (clientLocation.touched || clientSettingsForm.submitted)"
                  id="clientLocation"
                  name="location"
                  [(ngModel)]="clientForm.location"
                  #clientLocation="ngModel"
                  autocomplete="street-address"
                  maxlength="500"
                  required/>
                @if (clientLocation.invalid && (clientLocation.touched || clientSettingsForm.submitted)) {
                  <div class="invalid-feedback">Адресата е задолжителна.</div>
                }
              </div>

              <div class="col-12">
                <label class="form-label fw-semibold" for="clientEmail">
                  <fa-icon class="settings-label-icon" [icon]="faEnvelope"></fa-icon>
                  Контакт е-пошта
                </label>
                <input
                  class="form-control settings-control"
                  [class.is-invalid]="clientEmail.invalid && (clientEmail.touched || clientSettingsForm.submitted)"
                  id="clientEmail"
                  name="email"
                  [(ngModel)]="clientForm.email"
                  #clientEmail="ngModel"
                  type="email"
                  autocomplete="email"
                  maxlength="320"
                  email
                  required/>
                @if (clientEmail.errors?.['required'] && (clientEmail.touched || clientSettingsForm.submitted)) {
                  <div class="invalid-feedback">Е-поштата е задолжителна.</div>
                } @else if (clientEmail.errors?.['email'] && (clientEmail.touched || clientSettingsForm.submitted)) {
                  <div class="invalid-feedback">Внесете валидна е-пошта.</div>
                }
                <div class="form-text">Ова е контакт е-поштата на клиентот и може да се разликува од корисничката сметка.</div>
              </div>
            </div>

            <div class="settings-actions d-flex flex-column-reverse flex-sm-row justify-content-end gap-2 mt-4 pt-4">
              <button
                type="button"
                class="btn app-btn-soft px-4"
                [disabled]="savingClient || !clientSettingsForm.dirty"
                (click)="resetClient(clientSettingsForm)">
                <fa-icon class="me-2" [icon]="faRotateLeft"></fa-icon>
                Откажи промени
              </button>
              <button
                type="submit"
                class="btn primary-btn settings-primary-button px-4"
                [disabled]="savingClient || clientSettingsForm.invalid || !clientSettingsForm.dirty">
                @if (savingClient) {
                  <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                  Зачувување...
                } @else {
                  <fa-icon class="me-2" [icon]="faFloppyDisk"></fa-icon>
                  Зачувај промени
                }
              </button>
            </div>
          </form>
        </div>
      } @else {
        <div role="tabpanel">
          <div class="settings-section-heading mb-4">
            <h2 class="h4 fw-bold mb-1">Промена на лозинка</h2>
            <p class="text-muted mb-0">Користете единствена лозинка што не ја користите на друго место.</p>
          </div>

          <div class="security-note d-flex align-items-center gap-3 mb-4">
            <span class="security-note-icon"><fa-icon [icon]="faLock"></fa-icon></span>
            <div>
              <div class="fw-bold">Најавени сте како {{ settings?.userEmail }}</div>
              <small>По промената, сите други активни сесии ќе бидат одјавени.</small>
            </div>
          </div>

          @if (passwordError) {
            <app-error-alert class="d-block mb-4" title="Лозинката не е променета" [message]="passwordError"></app-error-alert>
          }
          @if (passwordSuccess) {
            <div class="alert settings-success-alert d-flex align-items-center gap-2 mb-4" role="status">
              <fa-icon [icon]="faCheck"></fa-icon>
              <span>{{ passwordSuccess }}</span>
            </div>
          }

          <form #changePasswordForm="ngForm" (ngSubmit)="changePassword(changePasswordForm)" novalidate>
            <div class="password-form">
              <div>
                <label class="form-label fw-semibold" for="currentPassword">Сегашна лозинка</label>
                <div class="password-control-wrap">
                  <input
                    class="form-control settings-control password-control"
                    [class.is-invalid]="currentPassword.invalid && (currentPassword.touched || changePasswordForm.submitted)"
                    id="currentPassword"
                    name="currentPassword"
                    [(ngModel)]="passwordForm.currentPassword"
                    #currentPassword="ngModel"
                    [type]="showCurrentPassword ? 'text' : 'password'"
                    autocomplete="current-password"
                    maxlength="200"
                    required/>
                  <button
                    type="button"
                    class="password-toggle"
                    (click)="showCurrentPassword = !showCurrentPassword"
                    [attr.aria-label]="showCurrentPassword ? 'Сокриј ја сегашната лозинка' : 'Прикажи ја сегашната лозинка'">
                    <fa-icon [icon]="showCurrentPassword ? faEyeSlash : faEye"></fa-icon>
                  </button>
                </div>
                @if (currentPassword.invalid && (currentPassword.touched || changePasswordForm.submitted)) {
                  <div class="invalid-feedback d-block">Сегашната лозинка е задолжителна.</div>
                }
              </div>

              <div>
                <label class="form-label fw-semibold" for="newPassword">Нова лозинка</label>
                <div class="password-control-wrap">
                  <input
                    class="form-control settings-control password-control"
                    [class.is-invalid]="newPassword.invalid && (newPassword.touched || changePasswordForm.submitted)"
                    id="newPassword"
                    name="newPassword"
                    [(ngModel)]="passwordForm.newPassword"
                    #newPassword="ngModel"
                    [type]="showNewPassword ? 'text' : 'password'"
                    autocomplete="new-password"
                    minlength="6"
                    maxlength="30"
                    [pattern]="passwordPattern"
                    required/>
                  <button
                    type="button"
                    class="password-toggle"
                    (click)="showNewPassword = !showNewPassword"
                    [attr.aria-label]="showNewPassword ? 'Сокриј ја новата лозинка' : 'Прикажи ја новата лозинка'">
                    <fa-icon [icon]="showNewPassword ? faEyeSlash : faEye"></fa-icon>
                  </button>
                </div>
              </div>

              <div class="password-rules" aria-label="Барања за лозинката">
                <span [class.met]="hasMinimumLength()"><fa-icon [icon]="faCheck"></fa-icon> 6–30 знаци</span>
                <span [class.met]="hasUppercaseLetter()"><fa-icon [icon]="faCheck"></fa-icon> Голема буква</span>
                <span [class.met]="hasLowercaseLetter()"><fa-icon [icon]="faCheck"></fa-icon> Мала буква</span>
                <span [class.met]="hasNumber()"><fa-icon [icon]="faCheck"></fa-icon> Број</span>
              </div>

              <div>
                <label class="form-label fw-semibold" for="confirmNewPassword">Потврди ја новата лозинка</label>
                <div class="password-control-wrap">
                  <input
                    class="form-control settings-control password-control"
                    [class.is-invalid]="passwordsDoNotMatch || (confirmNewPassword.invalid && (confirmNewPassword.touched || changePasswordForm.submitted))"
                    id="confirmNewPassword"
                    name="confirmNewPassword"
                    [(ngModel)]="passwordForm.confirmNewPassword"
                    #confirmNewPassword="ngModel"
                    [type]="showConfirmPassword ? 'text' : 'password'"
                    autocomplete="new-password"
                    minlength="6"
                    maxlength="30"
                    required/>
                  <button
                    type="button"
                    class="password-toggle"
                    (click)="showConfirmPassword = !showConfirmPassword"
                    [attr.aria-label]="showConfirmPassword ? 'Сокриј ја потврдата на лозинката' : 'Прикажи ја потврдата на лозинката'">
                    <fa-icon [icon]="showConfirmPassword ? faEyeSlash : faEye"></fa-icon>
                  </button>
                </div>
                @if (passwordsDoNotMatch) {
                  <div class="invalid-feedback d-block">Лозинките не се совпаѓаат.</div>
                } @else if (confirmNewPassword.invalid && (confirmNewPassword.touched || changePasswordForm.submitted)) {
                  <div class="invalid-feedback d-block">Потврдете ја новата лозинка.</div>
                }
              </div>
            </div>

            <div class="settings-actions d-flex justify-content-end mt-4 pt-4">
              <button
                type="submit"
                class="btn primary-btn settings-primary-button px-4"
                [disabled]="savingPassword || changePasswordForm.invalid || passwordsDoNotMatch">
                @if (savingPassword) {
                  <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                  Промена...
                } @else {
                  <fa-icon class="me-2" [icon]="faKey"></fa-icon>
                  Промени лозинка
                }
              </button>
            </div>
          </form>
        </div>
      }
    </div>
  </div>
</section>
